import { Component, input, output, computed, ChangeDetectionStrategy } from '@angular/core';

import { ViewModeConfig, ViewState, ActionOption } from '../types';
import { CardViewActionButtonComponent } from '../card-view-action-button';

@Component({
  selector: 'zag-card-view-controls',
  imports: [CardViewActionButtonComponent],
  templateUrl: './card-view-controls.component.html',
  styleUrls: ['./card-view-controls.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CardViewControlsComponent {
  /**
   * View mode configuration
   */
  config = input.required<ViewModeConfig>();

  /**
   * Current view state
   */
  viewState = input.required<ViewState>();

  /**
   * Which controls to show
   */
  showControls = input<{
    viewMode?: boolean;
    sort?: boolean;
    pageSize?: boolean;
  }>({ viewMode: true, sort: true, pageSize: true });

  /**
   * Emitted when view mode changes
   */
  viewModeChange = output<string>();

  /**
   * Emitted when sort option changes
   */
  sortChange = output<string>();

  /**
   * Emitted when page size changes
   */
  pageSizeChange = output<number>();

  /**
   * View mode options as ActionOption[]
   */
  viewModeOptions = computed<ActionOption[]>(() => {
    return this.config().modes.map(mode => ({
      id: mode.id,
      label: mode.label,
      icon: mode.icon,
    }));
  });

  /**
   * Sort options as ActionOption[]
   */
  sortOptions = computed<ActionOption[]>(() => {
    return (this.config().sortOptions || []).map(sort => ({
      id: sort.id,
      label: sort.label,
      icon: sort.icon,
    }));
  });

  /**
   * Page size options as ActionOption[]
   */
  pageSizeOptions = computed<ActionOption[]>(() => {
    return (this.config().pageSizeOptions || []).map(size => ({
      id: size.value.toString(),
      label: size.label,
      icon: size.icon,
      displayText: size.value === 0 ? '∞' : size.value,
    }));
  });

  /**
   * Whether to show view mode controls
   */
  showViewMode = computed(() => {
    return this.showControls().viewMode && this.config().modes.length > 0;
  });

  /**
   * Whether to show sort controls
   */
  showSort = computed(() => this.showControls().sort && (this.config().sortOptions?.length || 0) > 0);

  /**
   * Whether to show page size controls
   */
  showPageSize = computed(() => this.showControls().pageSize && (this.config().pageSizeOptions?.length || 0) > 0);

  /**
   * Handle view mode change
   */
  onViewModeChange(modeId: unknown): void {
    this.viewModeChange.emit(String(modeId));
  }

  /**
   * Handle sort change
   */
  onSortChange(sortId: unknown): void {
    this.sortChange.emit(String(sortId));
  }

  /**
   * Handle page size change
   */
  onPageSizeChange(pageSizeId: unknown): void {
    const pageSize = Number.parseInt(String(pageSizeId), 10);
    this.pageSizeChange.emit(pageSize);
  }
}
